import type { RevenueBasis, RevenueTiming } from '@/api/revenue'
import { Card, EmptyState, formatPercent, formatValue, type FormatOptions } from '@/design'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import type { GlobalFilters } from '@/lib/filters'

import { BASIS_NOUN, comparisonLabel, hourLabel, NULL_REASON_TEXT, sentence, WEEKDAY_SHORT } from '../format'
import { PanelNote } from '../pieces'
import { useRevenueTiming } from '../queries'
import { LifecyclePanelSlot } from '../slots'
import { HeatMap } from './HeatMap'
import { WeekdayProfile } from './WeekdayProfile'

/** "Sun 20:00 – 23:00", the week read as a loop (Sunday 23:00 runs into Monday). */
export function peakWindow(peak: NonNullable<RevenueTiming['peak']>): string {
  const endIndex = (peak.weekday * 24 + peak.startHour + peak.hours) % 168
  const endWeekday = Math.floor(endIndex / 24)
  const end = `${endWeekday === peak.weekday ? '' : `${WEEKDAY_SHORT[endWeekday]} `}${hourLabel(endIndex % 24)}`
  return `${WEEKDAY_SHORT[peak.weekday]} ${hourLabel(peak.startHour)} – ${end}`
}

function PeakCaption({ timing, money }: { timing: RevenueTiming; money: FormatOptions }) {
  if (!timing.peak) return <PanelNote>No peak · {NULL_REASON_TEXT[timing.peakNullReason ?? 'no_revenue']}</PanelNote>
  const { peak } = timing
  return (
    <p className="rv-peak" data-testid="timing-peak">
      <strong>Peak: {peakWindow(peak)}</strong> · {formatValue(peak.revenue, 'currency', money)}
      {peak.share !== null ? `, ${formatPercent(peak.share)} of the period’s revenue` : ''}
    </p>
  )
}

/**
 * The Timing tab (the proposal's screen 2): when revenue lands, by weekday and hour in the
 * tenant's timezone, and each weekday's average against the comparison. The lifecycle
 * panel beside them is the leakage session's (`LifecyclePanelSlot`).
 */
export function TimingTab({ filters, basis, money }: { filters: GlobalFilters; basis: RevenueBasis; money: FormatOptions }) {
  const read = useRevenueTiming(filters, basis)
  return (
    <div className="rv-grid">
      <Card className="rv-panel rv-panel--wide-half" title="Revenue by hour and weekday" subtitle={sentence(BASIS_NOUN[basis])} busy={read.isFetching}>
        <CardQueryBody query={read} loadingRows={7} errorDescription="Revenue by hour could not be loaded.">
          {({ data }) => (
            <>
              <p className="rv-muted rv-timezone">Tenant time ({data.timezone})</p>
              <HeatMap grid={data.grid} money={money} timezone={data.timezone} />
              <PeakCaption timing={data} money={money} />
            </>
          )}
        </CardQueryBody>
      </Card>
      <LifecyclePanelSlot filters={filters} basis={basis} money={money} />
      <Card className="rv-panel rv-panel--wide" title="Weekday profile" subtitle="Average revenue per weekday, this period against the comparison" busy={read.isFetching}>
        <CardQueryBody query={read} loadingRows={5} errorDescription="The weekday profile could not be loaded.">
          {({ data }) =>
            data.weekdayProfile ? (
              <WeekdayProfile
                profile={data.weekdayProfile}
                money={money}
                currentLabel="This period"
                previousLabel={data.previousRange && !data.previousUnavailable ? comparisonLabel(data, 'Comparison:') : null}
              />
            ) : (
              <EmptyState
                variant="no-data"
                compact
                title="Not available for this range"
                description={`${NULL_REASON_TEXT[data.weekdayProfileNullReason ?? 'range_too_short']}: an average per weekday needs every weekday in the range.`}
              />
            )
          }
        </CardQueryBody>
      </Card>
    </div>
  )
}
